import { type ReactNode, useId } from 'react'
import type { Observable } from 'rxjs'
import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListEmptyState,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Button } from '@/components/Button'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import { useIsSpacious } from '@/hooks/viewport-hooks'

/**
 * Props for {@link BusinessProfileCardListSection}.
 */
export interface BusinessProfileCardListSectionProps {
  /**
   * The section title.
   */
  title: string

  /**
   * The children to render (the list items).
   */
  children?: ReactNode

  /**
   * Optional action for the section header.
   */
  action?: ReactNode

  /**
   * Render the empty state.
   */
  renderEmptyState?: () => ReactNode

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A section within the business profile list.
 */
export function BusinessProfileCardListSection({
  title,
  children,
  action,
  isSkeleton,
  renderEmptyState,
  testId,
}: BusinessProfileCardListSectionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const headlineId = useId()
  return (
    <section className="pb-lg" data-test={testId}>
      <SectionHeadline id={headlineId} isSkeleton={isSkeleton} action={action}>
        {title}
      </SectionHeadline>
      <CardList
        aria-labelledby={headlineId}
        renderEmptyState={renderEmptyState}
      >
        {children}
      </CardList>
    </section>
  )
}

/**
 * Business Profile Card List Section Skeleton
 */
export function BusinessProfileCardListSectionSkeleton() {
  return (
    <BusinessProfileCardListSection isSkeleton title="Placeholder">
      <BusinessProfileCardListItem
        businessName="Placeholder"
        ein="00-0000000"
        isSkeleton
      />
    </BusinessProfileCardListSection>
  )
}

/**
 * Props for {@link ProvidedBusinessProfileCardListEmptyState}.
 */
export interface ProvidedBusinessProfileCardListEmptyStateProps {
  /**
   * Whether the user has existing profiles.
   */
  hasExistingProfiles?: boolean

  /**
   * Action to show in the empty state.
   */
  action?: ReactNode
}

/**
 * Empty state for the business profile list.
 */
export function ProvidedBusinessProfileCardListEmptyState({
  action,
  hasExistingProfiles,
}: ProvidedBusinessProfileCardListEmptyStateProps) {
  return (
    <CardListEmptyState
      action={action}
      title={
        hasExistingProfiles
          ? 'Use one of your existing business profiles, or add a new one.'
          : 'Add your business information for tax filing.'
      }
    />
  )
}

/**
 * Props for {@link BusinessProfileCardListItem}.
 */
export interface BusinessProfileCardListItemProps {
  /**
   * The business name.
   */
  businessName: string

  /**
   * The EIN (Employer Identification Number).
   */
  ein?: string

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Whether the profile is the current one.
   */
  isCurrent?: boolean
}

/**
 * Individual business profile card item.
 */
export function BusinessProfileCardListItem({
  businessName,
  ein,
  onAction,
  action,
  isDisabled,
  isSkeleton,
  isCurrent,
}: BusinessProfileCardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const einDisplay = ein ? `EIN ending in ${ein.slice(-4)}` : null
  const textValue = einDisplay
    ? `${businessName} - ${einDisplay}`
    : businessName
  const isSpacious = useIsSpacious()
  const subtitle = einDisplay ?? 'Business profile'

  return (
    <CardListItem
      textValue={isSkeleton ? 'Placeholder' : textValue}
      onAction={isSkeleton ? undefined : onAction}
      isDisabled={isDisabled || isSkeleton}
      isActive={isCurrent}
    >
      <CardItemContent
        isSkeleton={isSkeleton}
        isInteractive={Boolean(onAction && !action)}
        icon={
          isSpacious && <CardItemIcon skeleton={isSkeleton} name="business" />
        }
        extras={action}
      >
        <CardTitle>
          <AutoSkeleton isSkeleton={isSkeleton}>{businessName}</AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="sm-description">
          <AutoSkeleton isSkeleton={isSkeleton}>{subtitle}</AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Props for {@link OtherProfileDropdown}.
 */
export interface OtherProfileDropdownProps {
  /**
   * Callback to select the profile.
   */
  onSelect: () => unknown

  /**
   * Callback to delete the profile.
   */
  onDelete: () => unknown

  /**
   * Whether actions are disabled.
   */
  isDisabled?: boolean

  /**
   * Observable to trigger opening the dropdown.
   */
  open$?: Observable<unknown>
}

/**
 * Dropdown for other business profiles.
 */
export function OtherProfileDropdown({
  onSelect,
  onDelete,
  isDisabled,
  open$,
}: OtherProfileDropdownProps) {
  return (
    <Dropdown
      title="More options"
      open$={open$}
      trigger={
        <IconButton
          sm
          variant="no-hover"
          name="more"
          label="Actions"
          isDisabled={isDisabled}
        />
      }
    >
      <DropdownItem icon="tick" onAction={onSelect}>
        Use this profile
      </DropdownItem>
      <DropdownItem icon="delete" onAction={onDelete}>
        Delete
      </DropdownItem>
    </Dropdown>
  )
}

/**
 * Props for {@link AddNewProfileButton}.
 */
export interface AddNewProfileButtonProps {
  /**
   * Callback when the button is pressed.
   */
  onPress: () => unknown
  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean
}

/**
 * Button to add a new business profile.
 */
export function AddNewProfileButton({
  onPress,
  isDisabled,
}: AddNewProfileButtonProps) {
  return (
    <Button
      label="New business"
      variant="text"
      onPress={onPress}
      iconLeft="add"
      isDisabled={isDisabled}
    />
  )
}
